import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { VAULT_PARAMS_NS as NS } from './constants';
import type { ParamsEditor } from './useVaultParams';

export const ConfirmChangesDialog = ({
  editor,
}: {
  editor: Pick<ParamsEditor<unknown>, 'open' | 'step' | 'changes' | 'saving' | 'back' | 'confirm'>;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');

  return (
    <Dialog
      open={editor.open && editor.step === 'confirm'}
      onClose={editor.saving ? undefined : editor.back}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      <DialogTitle>{translate(`${NS}.confirm.title`)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">{translate(`${NS}.confirm.description`)}</Alert>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate(`${NS}.confirm.field`)}</TableCell>
                  <TableCell>{translate(`${NS}.confirm.before`)}</TableCell>
                  <TableCell>{translate(`${NS}.confirm.after`)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editor.changes.map((change) => (
                  <TableRow key={change.key}>
                    <TableCell>{translate(change.label)}</TableCell>
                    <TableCell
                      sx={{
                        color: 'text.secondary',
                        textDecoration: 'line-through',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {change.before}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {change.after}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={editor.back} disabled={editor.saving}>
          {translate(`${NS}.back`)}
        </Button>
        <LoadingButton
          variant="contained"
          onClick={editor.confirm}
          disabled={editor.saving}
          loading={editor.saving}
        >
          {translate(`${NS}.confirm.confirm`)}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
